import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chatsdet/ui';
import {
  fetchSchedules,
  fetchScheduleRuns,
  fetchJobDefinitions,
  createSchedule,
  deleteSchedule,
  runScheduleNow,
  updateSchedule,
} from './api.js';
import type { ScheduleUpdateData } from './types.js';

export function useScheduledJobs(selectedScheduleId: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const schedulesQuery = useQuery({
    queryKey: ['schedules'],
    queryFn: fetchSchedules,
    refetchInterval: 10_000,
  });

  const jobDefinitionsQuery = useQuery({
    queryKey: ['superadmin-job-definitions'],
    queryFn: fetchJobDefinitions,
  });

  const scheduleRunsQuery = useQuery({
    queryKey: ['schedule-runs', selectedScheduleId],
    queryFn: () => fetchScheduleRuns(selectedScheduleId!),
    enabled: Boolean(selectedScheduleId),
    refetchInterval: 5000,
  });

  const createMutation = useMutation({
    mutationFn: createSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const runNowMutation = useMutation({
    mutationFn: runScheduleNow,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      queryClient.invalidateQueries({ queryKey: ['schedule-runs'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScheduleUpdateData }) =>
      updateSchedule(id, data),
    onSuccess: () => {
      toast.success('Schedule updated successfully');
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  return {
    schedulesQuery,
    jobDefinitionsQuery,
    scheduleRunsQuery,
    createMutation,
    deleteMutation,
    runNowMutation,
    updateMutation,
  };
}
